import React, { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  flexRender,
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  Row,
} from "@tanstack/react-table";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  loading?: boolean;
  onRowClick?: (row: TData) => void;
  /** When set, rows can expand and this content renders under the parent row. */
  renderExpandedRow?: (row: TData) => React.ReactNode;
  /** Optional: control which rows show an expand control. Defaults to all. */
  getRowCanExpand?: (row: TData) => boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  loading = false,
  onRowClick,
  renderExpandedRow,
  getRowCanExpand,
}: DataTableProps<TData, TValue>) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const canExpandEnabled = Boolean(renderExpandedRow);

  const tableColumns = React.useMemo<ColumnDef<TData, TValue>[]>(() => {
    if (!canExpandEnabled) return columns;

    const expandColumn: ColumnDef<TData, TValue> = {
      id: "expander",
      header: () => null,
      cell: ({ row }) => {
        if (!row.getCanExpand()) return null;

        return (
          <button
            type="button"
            aria-label={row.getIsExpanded() ? "Σύμπτυξη" : "Ανάπτυξη"}
            onClick={(e) => {
              e.stopPropagation();
              row.toggleExpanded();
            }}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
          >
            <span
              className={`text-xs transition-transform ${
                row.getIsExpanded() ? "rotate-90" : ""
              }`}
            >
              ▶
            </span>
          </button>
        );
      },
      size: 40,
    };

    return [expandColumn, ...columns];
  }, [columns, canExpandEnabled]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      columnFilters,
      expanded,
    },
    onColumnFiltersChange: setColumnFilters,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: canExpandEnabled
      ? (row: Row<TData>) =>
          getRowCanExpand ? getRowCanExpand(row.original) : true
      : undefined,
  });

  if (loading) {
    return (
      <div className="w-full p-8 text-center text-slate-500 font-medium">
        Φόρτωση δεδομένων...
      </div>
    );
  }

  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <div className="w-full overflow-x-auto rounded-lg border border-slate-200 shadow-sm bg-white">
      <table className="w-full text-left text-sm text-slate-600">
        <thead className="bg-primary text-xs text-slate-700 border-b border-slate-200">
          {table.getHeaderGroups().map((headerGroup) => (
            <React.Fragment key={headerGroup.id}>
              <tr className="border-b border-slate-200">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-3 font-bold uppercase tracking-wider text-slate-700"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>

              <tr className="bg-slate-100/60 border-b border-slate-200">
                {headerGroup.headers.map((header) => (
                  <th
                    key={`filter-${header.id}`}
                    className="px-4 py-2 font-normal"
                  >
                    {header.column.getCanFilter() &&
                    header.id !== "select" &&
                    header.id !== "actions" &&
                    header.id !== "expander" ? (
                      <input
                        type="text"
                        value={(header.column.getFilterValue() as string) ?? ""}
                        onChange={(e) =>
                          header.column.setFilterValue(e.target.value)
                        }
                        placeholder="Φίλτρο..."
                        className="w-full px-2.5 py-1 text-xs font-normal normal-case border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white text-slate-800 shadow-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            </React.Fragment>
          ))}
        </thead>
        <tbody className="divide-y divide-slate-100">
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <React.Fragment key={row.id}>
                <tr
                  onClick={() => {
                    if (canExpandEnabled && row.getCanExpand()) {
                      row.toggleExpanded();
                      return;
                    }
                    onRowClick?.(row.original);
                  }}
                  className={`transition-colors group ${
                    onRowClick || (canExpandEnabled && row.getCanExpand())
                      ? "cursor-pointer hover:bg-lightprimary"
                      : "hover:bg-lightprimary"
                  }`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3 align-middle">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>

                {row.getIsExpanded() && renderExpandedRow ? (
                  <tr className="bg-slate-50/80">
                    <td colSpan={columnCount} className="px-4 py-3">
                      {renderExpandedRow(row.original)}
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            ))
          ) : (
            <tr>
              <td
                colSpan={Math.max(columns.length, 1)}
                className="px-4 py-8 text-center text-slate-400 font-medium"
              >
                Δεν βρέθηκαν εγγραφές.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
